import { useEffect, useState } from 'react';
import { getSession } from '~/lib/auth';

/**
 * Users — invite/manage who has backend access.
 *
 * Two roles: admin (full access + can manage users) and editor (content
 * only). Admins see the invite form and per-row actions; editors see the
 * list read-only.
 *
 * Invited users don't need to do anything special: inviting creates their
 * admin_users row, then they go to /login and ask for a sign-in code the
 * normal way (POST /api/auth/request-link only emails addresses that have
 * a row, so the row IS the access grant).
 */

/** dev = HDM (superset of admin, owns page layout). Only a dev can grant
 * the dev role or change/remove a dev account — the API enforces it; the
 * UI just doesn't offer what would be refused. */
type Role = 'dev' | 'admin' | 'editor';

const ROLE_LABEL: Record<Role, string> = { dev: 'Dev (HDM)', admin: 'Admin', editor: 'Editor' };

interface AdminUser {
  user_id: string;
  email: string;
  role: Role;
  created_at: string;
  last_seen_at: string | null;
}

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';

const cardStyle: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: 24,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 14,
  padding: '10px 12px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  background: 'var(--card)',
  color: 'var(--text)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: 'var(--primary)',
  textTransform: 'uppercase',
  display: 'block',
  marginBottom: 4,
};

export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [myRole, setMyRole] = useState<Role | null>(null);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('admin');
  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      setMyUserId(session.user.id);

      const res = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = (await res.json()) as { users: AdminUser[] };
      setUsers(data.users ?? []);

      const me = data.users?.find((u) => u.user_id === session.user.id);
      setMyRole(me?.role ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteMessage(null);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Server returned ${res.status}`);
      }
      setInviteMessage(`Invited ${inviteEmail} as ${inviteRole}. They can now sign in at /login.`);
      setInviteEmail('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setInviting(false);
    }
  }

  async function changeRole(userId: string, role: Role) {
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Server returned ${res.status}`);
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function revoke(userId: string, email: string) {
    if (!confirm(`Revoke backend access for ${email}? They can be re-invited later.`)) return;
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Server returned ${res.status}`);
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const isAdmin = myRole === 'admin' || myRole === 'dev';
  const isDev = myRole === 'dev';
  const assignable: Role[] = isDev ? ['dev', 'admin', 'editor'] : ['admin', 'editor'];

  return (
    <div style={{ maxWidth: 900, display: 'grid', gap: 24 }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 32, margin: 0 }}>Team</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Who has access to this backend. Admins can edit everything and manage the team;
          editors can edit content but not bookings, clients, or team membership.
        </p>
      </div>

      {error && (
        <div style={{ ...cardStyle, borderColor: '#c44', color: '#c44' }}>{error}</div>
      )}

      {isAdmin && <PermissionsMatrix />}

      {isAdmin && (
        <form onSubmit={invite} style={cardStyle}>
          <h2 style={{ margin: '0 0 16px', fontFamily: 'var(--font-display)', fontSize: 22 }}>
            Invite a user
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="channa@valleyvitalitywellness.com"
                style={inputStyle}
                disabled={inviting}
              />
            </div>
            <div>
              <label style={labelStyle}>Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
                style={inputStyle}
                disabled={inviting}
              >
                {assignable.map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              style={{
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: inviting ? 'wait' : 'pointer',
                opacity: inviting || !inviteEmail.trim() ? 0.5 : 1,
              }}
            >
              {inviting ? 'Inviting…' : 'Invite'}
            </button>
          </div>
          {inviteMessage && (
            <p style={{ marginTop: 12, fontSize: 13, color: 'var(--primary)' }}>{inviteMessage}</p>
          )}
        </form>
      )}

      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 16px', fontFamily: 'var(--font-display)', fontSize: 22 }}>
          {loading ? 'Loading team…' : `Team (${users.length})`}
        </h2>
        {!loading && users.length === 0 && (
          <p style={{ color: 'var(--text-muted)' }}>No users yet.</p>
        )}
        {users.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>Added</th>
                {isAdmin && (
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.user_id === myUserId;
                const canManage = isAdmin && !isSelf && (u.role !== 'dev' || isDev);
                return (
                  <tr key={u.user_id} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '12px 0' }}>
                      {u.email}
                      {isSelf && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(you)</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 0' }}>
                      {canManage ? (
                        <select
                          value={u.role}
                          onChange={(e) => void changeRole(u.user_id, e.target.value as Role)}
                          style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
                        >
                          {assignable.map((r) => (
                            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                          ))}
                        </select>
                      ) : (
                        <span>{ROLE_LABEL[u.role] ?? u.role}</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 0', fontSize: 13, color: 'var(--text-muted)' }}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: '12px 0', textAlign: 'right' }}>
                        {canManage && (
                          <button
                            type="button"
                            onClick={() => void revoke(u.user_id, u.email)}
                            style={{
                              padding: '6px 12px',
                              fontSize: 13,
                              color: '#c44',
                              background: 'transparent',
                              border: '1px solid var(--line)',
                              borderRadius: 6,
                              cursor: 'pointer',
                            }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Admin-only breakdown of what each role can do. Mirrors the RLS policies
// in migration 0012_admin_users.sql; when those change, update both.
function PermissionsMatrix() {
  const rows: { label: string; admin: boolean; editor: boolean }[] = [
    { label: 'Edit site content (pages, drip menu, services, FAQs, how-it-works, app lessons)', admin: true, editor: true },
    { label: 'View team membership',                              admin: true, editor: true },
    { label: 'Invite users, change roles, revoke access',          admin: true, editor: false },
    { label: 'View and manage bookings',                           admin: true, editor: false },
    { label: 'View and manage clients',                            admin: true, editor: false },
    { label: 'View payments and Stripe records',                   admin: true, editor: false },
    { label: 'View audit log',                                     admin: true, editor: false },
    { label: 'Manage Google Calendar / OAuth integrations',        admin: true, editor: false },
  ];

  const cellHead: React.CSSProperties = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    borderBottom: '1px solid var(--line)',
  };
  const cellRow: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: 13.5,
    borderBottom: '1px solid var(--line-soft)',
  };
  const yes: React.CSSProperties = { color: 'var(--primary)', fontWeight: 600 };
  const no: React.CSSProperties = { color: 'var(--text-muted)' };

  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--line)',
      borderRadius: 12,
      padding: 24,
    }}>
      <h2 style={{ margin: '0 0 4px', fontFamily: 'var(--font-display)', fontSize: 22 }}>
        Role permissions
      </h2>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 16px', fontSize: 13 }}>
        What each role can do, enforced both in the UI and at the database (RLS) level.
        Only admins see this breakdown.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={cellHead}>Capability</th>
            <th style={{ ...cellHead, textAlign: 'center', width: 90 }}>Admin</th>
            <th style={{ ...cellHead, textAlign: 'center', width: 90 }}>Editor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={cellRow}>{r.label}</td>
              <td style={{ ...cellRow, textAlign: 'center' }}>
                <span style={r.admin ? yes : no}>{r.admin ? '✓' : '—'}</span>
              </td>
              <td style={{ ...cellRow, textAlign: 'center' }}>
                <span style={r.editor ? yes : no}>{r.editor ? '✓' : '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
